import { computed, Injectable, signal } from '@angular/core';
import { ClientWorkPackageStatus } from '@client/work-packages/enums/client-work-package-status.enum';
import { ClientResponse, ClientWorkPackageResponse } from '@client/work-packages/interfaces/client-data.interface';
import { ClientWebsocketEvents, ClientWebsocketMethods } from '@client/shared/interfaces/client-websocket.interface';
import { BaseWebsocketHubClient } from '@client/shared/services/base-websocket-hub-client';
import {
    OperationDto,
    OperationFileDto,
    OperationsPage,
    WorkPackageResponse,
    WorkPackageStatsDto,
} from '@client/work-packages/interfaces/work-package-detail.interface';
import { OperationStatus, ShareReceivedDto } from '@privanot/shared-models';
import { SharingKeyExportDto, SharingKeyStatusDto } from '@client/shared/interfaces/sharing.interface';
import { TunnelHealthDto } from '@client/shared/interfaces/tunnel-health.interface';
import { BastionEnrollmentState, BastionEnrollmentStateDto } from '@client/shared/interfaces/bastion-enrollment.interface';

const CLIENTS_HUB_PATH = 'client';

@Injectable({ providedIn: 'root' })
export class ClientService extends BaseWebsocketHubClient<ClientWebsocketMethods, ClientWebsocketEvents> {
    protected override hubPath: string = CLIENTS_HUB_PATH;

    private readonly _clientData = signal<ClientResponse | null>(null);
    private clientDataLoaded = signal<boolean>(false);
    private isFetching: Promise<void> | null = null;

    private readonly _bastionEnrollmentState = signal<BastionEnrollmentState | null>(null);

    public readonly clientData = this._clientData.asReadonly();
    public readonly loaded = this.clientDataLoaded.asReadonly();
    public readonly bastionEnrollmentState = this._bastionEnrollmentState.asReadonly();
    public readonly activeWorkPackages = computed(() =>
        this._clientData()?.workPackages?.filter((p) => p.status !== ClientWorkPackageStatus.CLOSED),
    );
    public readonly archivedWorkPackages = computed(() =>
        this._clientData()?.workPackages?.filter((p) => p.status === ClientWorkPackageStatus.CLOSED),
    );

    public async refreshData(): Promise<void> {
        if (this.isFetching) {
            return this.isFetching;
        }

        this.isFetching = (async () => {
            try {
                const apiData = await this.getClientData();
                this._clientData.set(apiData);
                this.clientDataLoaded.set(true);
            } finally {
                this.isFetching = null;
            }
        })();

        return this.isFetching;
    }

    public async ensureClientDataLoaded(): Promise<void> {
        if (this.clientDataLoaded()) {
            return;
        }

        await this.refreshData();
    }

    public async getWorkPackageById(id: string): Promise<ClientWorkPackageResponse | null> {
        return this._clientData()?.workPackages?.find((workPackage) => workPackage.id === id) ?? null;
    }

    public async getWorkPackage(id: string): Promise<WorkPackageResponse> {
        return this.invokeMethod('getWorkPackage', id);
    }

    public async getWorkPackageStats(id: string): Promise<WorkPackageStatsDto> {
        return this.invokeMethod('getWorkPackageStats', id);
    }

    public async getOperations(id: string, pageNumber: number, pageSize: number): Promise<OperationsPage> {
        const page = await this.invokeMethod('getOperations', id, pageNumber, pageSize);
        return { ...page, items: page.items.map(this.normalizeOperationStatus) };
    }

    public async getOperation(operationId: string): Promise<OperationDto> {
        return this.normalizeOperationStatus(await this.invokeMethod('getOperation', operationId));
    }

    public async getOperationFile(operationFileId: string): Promise<OperationFileDto> {
        return this.invokeMethod('getOperationFile', operationFileId);
    }

    private async getClientData(): Promise<ClientResponse> {
        return await this.invokeMethod('getClientData');
    }

    public getTunnelHealth(): Promise<TunnelHealthDto> {
        return this.invokeMethod('health');
    }

    public onTunnelStatus(handler: (dto: TunnelHealthDto) => void): () => void {
        return this.on('tunnel_status', handler);
    }

    public async refreshBastionEnrollmentState(): Promise<BastionEnrollmentState> {
        const dto = await this.invokeMethod('getBastionEnrollmentState');
        this._bastionEnrollmentState.set(dto.state);
        return dto.state;
    }

    public onBastionEnrollmentState(handler: (dto: BastionEnrollmentStateDto) => void): () => void {
        return this.on('bastion_enrollment_state', (dto) => {
            this._bastionEnrollmentState.set(dto.state);
            handler(dto);
        });
    }

    public generateSharingKeys(): Promise<SharingKeyStatusDto> {
        return this.invokeMethod('generateSharingKeys');
    }

    public generateSharingKeysForExport(): Promise<SharingKeyExportDto> {
        return this.invokeMethod('generateSharingKeysForExport');
    }

    public importSharingKeys(encryptionPemPrivate: string): Promise<SharingKeyStatusDto> {
        return this.invokeMethod('importSharingKeys', encryptionPemPrivate);
    }

    public getSharingKeyStatus(): Promise<SharingKeyStatusDto> {
        return this.invokeMethod('getSharingKeyStatus');
    }

    public onSharingKeyLoadFailed(handler: (reason: string) => void): () => void {
        return this.on('sharingKeyLoadFailed', handler);
    }

    public onSharingKeyRegistered(handler: (status: SharingKeyStatusDto) => void): () => void {
        return this.on('sharingKeyRegistered', handler);
    }

    public onSharingKeyMismatch(handler: (status: SharingKeyStatusDto) => void): () => void {
        return this.on('sharingKeyMismatch', handler);
    }

    public onShareReceived(handler: (dto: ShareReceivedDto) => void): () => void {
        return this.on('shareReceived', handler);
    }

    // Treat PARTIAL_SUCESS as failed
    private normalizeOperationStatus = (op: OperationDto): OperationDto => ({
        ...op,
        status: op.status === OperationStatus.PARTIAL_SUCCESS ? OperationStatus.FAILED : op.status,
    });
}
